import { SectionLabel } from "../components/label";
import { AnimatePresence, motion } from "framer-motion";
import useStore from "../store";
import { ComponentProps, FC, useCallback, useState } from "react";
import { Button } from "../components/form";
import { Plus } from "../icons";
import { Security, SecurityFormModal } from ".";

const Securities: FC = () => {
  const [isAdding, setAdding] = useState(false);
  const showModal = () => setAdding(true);
  const closeModal = () => setAdding(false);

  const [
    ApplicantParty,
    Loans,
    Securities,
    addLoan,
    addFinancial,
    addSecurity,
    addLoanSecurityMapping,
  ] = useStore(
    useCallback(
      (state) => [
        Array.from(state.ApplicantParties.values())[0],
        Array.from(state.Loans.values()),
        Array.from(state.Security.values()),
        state.addLoan,
        state.addFinancial,
        state.addSecurity,
        state.addLoanSecurityMapping,
      ],
      []
    )
  );
  const hasApplicant = typeof ApplicantParty !== "undefined";

  const handleAddSecurity: ComponentProps<
    typeof SecurityFormModal
  >["onSave"] = (value) => {
    if (!value.Loans?.length || !value?.Security) return;

    const newSecurity = addSecurity(value.Security);

    (value.Loans || []).forEach((Loan) => {
      const newLoan = addLoan(Loan);

      if (newLoan && newSecurity) {
        addLoanSecurityMapping({
          LoanId: newLoan.LoanId,
          SecurityId: newSecurity?.SecurityId,
        });
      }

      if (
        newLoan &&
        newLoan.LoanType === "Existing" &&
        newLoan.LendingAction !== null &&
        Loan?.Liability
      ) {
        addFinancial(ApplicantParty._id, {
          type: "Liability",
          value: {
            Lender: Loan?.Liability?.Lender ?? null,
            Balance: Loan?.Liability?.Balance ?? null,
            Limit: Loan?.Liability?.Limit ?? null,
            InterestRate: Loan?.Liability?.InterestRate ?? null,
            RepaymentAmount: Loan?.Liability?.RepaymentAmount ?? null,
            RepaymentFrequency: Loan?.Liability?.RepaymentFrequency ?? null,
            RepaymentType: Loan?.Liability?.RepaymentType ?? null,
            TotalTermMonths: Loan?.Liability?.TotalTermMonths ?? null,
            StartDate: Loan?.Liability?.StartDate ?? null,
            IsRevolvingCredit: Loan?.Liability?.IsRevolvingCredit ?? null,
            InterestOnlyTermMonths:
              Loan?.Liability?.InterestOnlyTermMonths ?? null,
            ApplicantId: Array.from(ApplicantParty.Applicant.values())[0]
              .ApplicantId,
            LiabilityType: "HomeLoan",
            LoanId: newLoan.LoanId,
          },
        });
      }
    });

    closeModal();
  };

  return (
    <section>
      <header className="flex items-center justify-between">
        <SectionLabel className="leading-[1.75rem] tracking-[-0.5px] desktop:text-base desktop:leading-[1.75rem]">
          Securities
        </SectionLabel>
      </header>
      <main className="mt-2 space-y-[15px]">
        <AnimatePresence>
          {(Securities || []).map((security, idx) => (
            <motion.div
              key={security.SecurityId}
              initial={{ height: 0, opacity: 0 }}
              exit={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <Security SecurityId={security.SecurityId} />
            </motion.div>
          ))}
        </AnimatePresence>
      </main>
      <footer className="mt-4">
        <AnimatePresence>
          {hasApplicant && (
            <>
              <SecurityFormModal
                title="Add security"
                open={isAdding}
                handleClose={closeModal}
                onSave={handleAddSecurity}
              />
              {Loans.length < 1 ? (
                <Button onClick={showModal} withIcon={<Plus fill="#ffffff" />}>
                  Add Security
                </Button>
              ) : (
                <Button
                  onClick={showModal}
                  className="justify-start bg-transparent px-0 py-0 font-bold text-accent"
                >
                  <span
                    style={{ borderWidth: "1.5px" }}
                    className="rounded-full border-accent p-[4px]"
                  >
                    <Plus fill="#42b4b4" />
                  </span>
                  Add Security
                </Button>
              )}
            </>
          )}
          {!hasApplicant && (
            <motion.div
              animate={{
                height: !hasApplicant ? "60px" : 0,
                opacity: !hasApplicant ? 1 : 0,
                padding: !hasApplicant ? "10px" : 0,
              }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="grid place-items-center rounded-[3px] bg-[#F7F4F9] text-center text-[13px] font-bold text-[#83748E]/50 desktop:text-sm"
            >
              <span>Add Applicant to get started</span>
            </motion.div>
          )}
        </AnimatePresence>
      </footer>
    </section>
  );
};

export default Securities;
